import React, { useState, useRef, useEffect } from 'react';
import {
  Form,
  InputGroup,
  Button,
  ListGroup,
  Spinner,
  Modal,
  Row,
  Col,
  Card,
  Badge
} from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import { buscarConsecutivo } from '../../api/common';
import { Alertassw } from '../Alertas/Alertassw';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

export const SearchBar = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showDropdown, setShowDropdown] = useState(false);
  const [seleccion, setSeleccion] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const containerRef = useRef(null);

  // Cerrar dropdown al click fuera del contenedor
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const searchDatabase = async () => {
    if (query.trim() === '') {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    try {
      const response = await buscarConsecutivo('EqInOt', { consecutivo: query });
      if (response.data.data && response.data.data.length > 0) {
        setResults(response.data.data);
        setShowDropdown(true);
      } else {
        setResults([]);
        setShowDropdown(false);
        Alertassw('error', 'Información no encontrada', 'Verifica e intenta de nuevo');
      }
    } catch (error) {
      console.error('Error en la búsqueda:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      searchDatabase();
    }
  };

  const handleSelect = (result) => {
    setSeleccion(result);
    setShowModal(true);
    setShowDropdown(false);
    setQuery('');
    setResults([]);
  };

  // Replica lo que hace useOtList al cambiar un filtro:
  // invalida la cache de datos y reescribe el state UI persistente.
  // Usa window.location.assign para forzar montaje fresco de Ot.js — así el
  // filtro se aplica también si el usuario ya estaba en /otlista/total.
  const verDetalleOt = () => {
    if (!seleccion?.numeroOrden) return;
    const userId = userData?._id;
    if (userId) {
      localStorage.removeItem(`ot_list_cache_${userId}`);
      const key = `ot_list_ui_${userId}`;
      let prev = {};
      try {
        prev = JSON.parse(localStorage.getItem(key)) || {};
      } catch (_) {
        prev = {};
      }
      localStorage.setItem(
        key,
        JSON.stringify({
          ...prev,
          page: 1,
          filters: { numeroOrden: seleccion.numeroOrden }
        })
      );
    }
    setShowModal(false);
    window.location.assign('/otlista/total');
  };

  const irPerfilEquipo = () => {
    const eq = seleccion?.equiposDetalles?.equipo;
    const equipoId = eq?._id || eq;
    if (equipoId) {
      setShowModal(false);
      navigate(`/perfilEq?_id=${equipoId}`);
    }
  };

  const verIngreso = () => {
    const ingresoId = seleccion?.equiposDetalles?.ingreso;
    const numeroOrden = seleccion?.numeroOrden;
    if (!ingresoId || !numeroOrden) return;
    setShowModal(false);
    navigate(`/Ingresos/${ingresoId}?orden=${numeroOrden}&origen=ingresos`);
  };

  const eqDetalle = seleccion?.equiposDetalles;
  const eqInfo = eqDetalle?.equipo || {};
  const snap = eqDetalle?.snapshot || {};
  const fechaCreacion = seleccion?.createdAt
    ? new Date(seleccion.createdAt).toLocaleDateString('es-CO')
    : '-';

  return (
    <div
      className="search-bar"
      ref={containerRef}
      style={{ position: 'relative' }}
    >
      <InputGroup size="sm">
        <Form.Control
          id="inputBusqueda"
          type="text"
          placeholder="Buscar consecutivo..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          aria-label="Buscar"
        />
        <Button variant="outline-secondary" onClick={searchDatabase} size="sm">
          <FaSearch />
        </Button>
        {loading && (
          <InputGroup.Text>
            <Spinner animation="border" size="sm" />
          </InputGroup.Text>
        )}
      </InputGroup>

      {showDropdown && results.length > 0 && (
        <ListGroup
          className="position-absolute"
          style={{
            top: '100%',
            left: 0,
            right: 0,
            minWidth: '380px',
            marginTop: '4px',
            zIndex: 1080,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            maxHeight: '320px',
            overflowY: 'auto'
          }}
        >
          {results.map((r, i) => {
            const det = r.equiposDetalles || {};
            const eq = det.equipo || {};
            return (
              <ListGroup.Item
                action
                key={r._id || i}
                onClick={() => handleSelect(r)}
              >
                <strong>{det.consecutivo}</strong>{' '}
                {eq.item?.nombre || det.snapshot?.item || ''}
                <small className="text-muted d-block">
                  {eq.cliente?.nombre || ''} → OT {r.numeroOrden}
                </small>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      )}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className='w-100'>Detalle de búsqueda</Modal.Title>
        </Modal.Header>
        <Modal.Body className='w-100'>
          {seleccion && (
            <Row>
              <Col md={6} className="mb-3 mb-md-0">
                <Card className="w-100">
                  <Card.Header className="bg-primary text-white w-100">
                    <strong>Equipo</strong>
                  </Card.Header>
                  <Card.Body className='w-100'>
                    <div className="mb-2">
                      <small className="text-muted d-block">Consecutivo</small>
                      <strong>{eqDetalle?.consecutivo || '-'}</strong>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block">Item</small>
                      {snap.item || eqInfo.item?.nombre || '-'}
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block">Marca</small>
                      {snap.marca || eqInfo.marca || '-'}
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block">Modelo</small>
                      {snap.modelo || eqInfo.modelo || '-'}
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block">Serie</small>
                      {snap.serial || eqInfo.serie || '-'}
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block">Ubicación</small>
                      {snap.ubicacion || eqInfo.ubicacion || '-'}
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block">Estado</small>
                      <Badge bg="secondary">{eqDetalle?.estado || '-'}</Badge>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="w-100">
                  <Card.Header className="bg-secondary text-white w-100">
                    <strong>Orden de Trabajo</strong>
                  </Card.Header>
                  <Card.Body className='w-100'>
                    <div className="mb-2">
                      <small className="text-muted d-block">Número de Orden</small>
                      <strong>{seleccion.numeroOrden || '-'}</strong>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block">Cliente</small>
                      {seleccion?.cliente?.nombre || eqInfo.cliente?.nombre || '-'}
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block">Fecha de creación</small>
                      {fechaCreacion}
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block">Estado</small>
                      <Badge bg="info">{seleccion.estado || '-'}</Badge>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer className='w-100'>
          <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
            Cerrar
          </Button>
          <Button
            variant="info"
            onClick={irPerfilEquipo}
            disabled={!eqInfo?._id && !eqInfo}
          >
            Ir al perfil del equipo
          </Button>
          {eqDetalle?.ingreso && (
            <Button variant="warning" onClick={verIngreso}>
              Ver Ingreso
            </Button>
          )}
          <Button variant="primary" onClick={verDetalleOt}>
            Ver detalle de la OT
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
